import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Play, Search, ChevronDown, Flag, Sparkles } from "lucide-react";
import { AdminShell } from "../components/admin/AdminShell";
import { Button } from "../components/ui/button";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { QuestionCard } from "../components/admin/QuestionCard";
import { AddQuestion } from "../components/admin/AddQuestion";
import { CopyrightReview } from "../components/admin/CopyrightReview";

const VIEWS = [["attention", "Needs attention"], ["reported", "User reports"], ["flagged", "AI flagged"], ["reviewed", "Reviewed"], ["all", "All questions"]];

export default function AdminQuestions() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [view, setView] = useState("attention");
  const [track, setTrack] = useState("");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [auditLimit, setAuditLimit] = useState(50);

  const loadSummary = () => api.get("/admin/questions/summary").then(({ data }) => setSummary(data)).catch(() => {});
  const load = (s = 0) => {
    setLoading(true);
    api.get("/admin/questions/review", { params: { view, track: track || undefined, q: q || undefined, skip: s, limit: 25 } })
      .then(({ data }) => { setItems(data.items); setTotal(data.total); setSkip(s); })
      .catch(() => toast.error("Could not load questions.")).finally(() => setLoading(false));
  };
  useEffect(() => { if (user?.role === "admin") loadSummary(); }, [user]);
  useEffect(() => { if (user?.role === "admin") load(0); }, [view, track, user]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (summary?.job?.status !== "running") return undefined;
    const t = setInterval(loadSummary, 3000);
    return () => clearInterval(t);
  }, [summary?.job?.status]);
  useEffect(() => { if (summary?.job?.status === "done") load(skip); }, [summary?.job?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const startAudit = async () => {
    try { await api.post("/admin/questions/audit", { track: track || null, limit: auditLimit, only_unaudited: true }); toast.success("Audit started."); loadSummary(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start audit."); }
  };
  const replace = (qid, patch) => setItems((xs) => xs.map((x) => (x.question.id === qid ? { ...x, ...patch } : x)));
  const remove = (qid) => { setItems((xs) => xs.filter((x) => x.question.id !== qid)); setTotal((t) => t - 1); loadSummary(); };
  const job = summary?.job;

  return (
    <AdminShell active="questions" testid="admin-questions-page">
      <div>
        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="admin-qc-summary">
            {[["Questions", summary.total], ["Audited", summary.audited], ["Unaudited", summary.unaudited], ["Open AI flags", summary.flagged_open], ["User reports", summary.reported], ["Disabled", summary.disabled]].map(([l, v]) => (
              <div key={l} className="bg-white border border-slate-200 rounded-xl p-3"><p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{l}</p><p className="font-heading text-2xl font-bold text-slate-900" data-testid={`admin-qc-stat-${l.toLowerCase().replace(/ /g, "-")}`}>{v}</p></div>
            ))}
          </div>
        )}

        <div className="mt-5 bg-slate-950 text-white rounded-xl p-4 flex flex-wrap items-center gap-3" data-testid="admin-qc-audit">
          <Sparkles className="w-4 h-4 text-amber-400" />
          <span className="text-sm font-semibold">AI audit</span>
          <select value={auditLimit} onChange={(e) => setAuditLimit(Number(e.target.value))} className="bg-white/10 border border-white/15 rounded-md text-sm px-2 py-1" data-testid="admin-qc-audit-limit">
            {[25, 50, 100, 200, 300].map((n) => <option key={n} value={n}>{`${n} unaudited questions`}</option>)}
          </select>
          <span className="text-xs text-slate-400">{track ? `in ${track}` : "across all tracks"} · ~6 questions per AI call</span>
          <Button size="sm" onClick={startAudit} disabled={job?.status === "running"} className="bg-amber-400 text-slate-950 hover:bg-amber-300 ml-auto" data-testid="admin-qc-audit-start">
            {job?.status === "running" ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> {job.done}/{job.total}</> : <><Play className="w-4 h-4 mr-1" /> Run audit</>}
          </Button>
          {job && job.status !== "running" && <span className="text-xs text-slate-400 w-full" data-testid="admin-qc-job-status">Last run: {job.status} · {job.done} audited{job.error ? ` · ${job.error}` : ""}</span>}
        </div>

        <div className="mt-5 flex"><AddQuestion tracks={summary?.tracks || []} onAdded={() => { loadSummary(); if (view === "all") load(0); }} /></div>
        <CopyrightReview />

        <div className="mt-6 flex flex-wrap items-center gap-2">
          <div className="flex gap-1 bg-white border border-slate-200 rounded-full p-1" data-testid="admin-qc-views">
            {VIEWS.map(([id, l]) => <button key={id} type="button" onClick={() => setView(id)} className={`text-xs font-semibold px-3 py-1.5 rounded-full ${view === id ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid={`admin-qc-view-${id}`}>{l}</button>)}
          </div>
          <select value={track} onChange={(e) => setTrack(e.target.value)} className="text-xs border border-slate-200 rounded-full px-3 py-2 bg-white" data-testid="admin-qc-track">
            <option value="">All tracks</option>
            {(summary?.tracks || []).map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <form onSubmit={(e) => { e.preventDefault(); load(0); }} className="flex items-center gap-1 ml-auto">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search question text" className="text-xs border border-slate-200 rounded-full px-3 py-2 bg-white w-56" data-testid="admin-qc-search" />
            <button type="submit" className="p-2 text-slate-500 hover:text-slate-900" data-testid="admin-qc-search-go"><Search className="w-4 h-4" /></button>
          </form>
        </div>

        <p className="mt-4 text-xs text-slate-500" data-testid="admin-qc-count">{loading ? "Loading…" : `${total} question${total === 1 ? "" : "s"}`}</p>
        <div className="mt-2 space-y-3" data-testid="admin-qc-list">
          {items.map((it) => <QuestionCard key={it.question.id} item={it} onChange={(patch) => replace(it.question.id, patch)} onRemove={() => remove(it.question.id)} />)}
          {!loading && items.length === 0 && <p className="text-sm text-slate-500 bg-white border border-slate-200 rounded-xl p-6 text-center" data-testid="admin-qc-empty"><Flag className="w-4 h-4 inline mr-1 text-slate-400" /> Nothing here right now.</p>}
        </div>
        {total > 25 && (
          <div className="mt-4 flex items-center justify-between text-sm">
            <Button variant="outline" size="sm" disabled={skip === 0} onClick={() => load(Math.max(0, skip - 25))} data-testid="admin-qc-prev">Previous</Button>
            <span className="text-slate-500">{skip + 1}–{Math.min(skip + 25, total)} of {total}</span>
            <Button variant="outline" size="sm" disabled={skip + 25 >= total} onClick={() => load(skip + 25)} data-testid="admin-qc-next">Next <ChevronDown className="w-4 h-4 -rotate-90" /></Button>
          </div>
        )}
      </div>
    </AdminShell>
  );
}
